import { z } from 'zod';

/**
 * Seven EXPLICIT actions, not a chat box.
 *
 * A generic assistant invites a producer to ask anything and leaves the product
 * responsible for whatever it answers. A fixed set of actions means each one has
 * a known input, a known output shape, a versioned prompt and an eval — and a
 * producer knows what they are about to get before they click.
 */
export const AiToolAction = z.enum([
  'FULL_SCRIPT_SYNOPSIS',
  'ONE_PAGE_SYNOPSIS',
  'SCENE_SYNOPSIS',
  'LOCATION_SUMMARY',
  'PRODUCTION_COMPLEXITY_SUMMARY',
  'LOCATION_EFFICIENCY_SUGGESTIONS',
  'GENERATE_LOCATION_BRIEF',
]);
export type AiToolAction = z.infer<typeof AiToolAction>;

/**
 * The four-way provenance every produced claim carries.
 *
 * Rendered distinctly, always. A producer reading a generated brief must be able
 * to tell at a glance which lines came from the screenplay, which the model
 * inferred, which a human confirmed, and which are waiting on someone — because
 * acting on an inference as though it were a script fact is how a production
 * turns up somewhere expecting a staircase that is not there.
 */
export const ClaimProvenance = z.enum([
  'FROM_SCRIPT',
  'AI_INFERENCE',
  'HUMAN_CONFIRMED',
  'NEEDS_CONFIRMATION',
]);
export type ClaimProvenance = z.infer<typeof ClaimProvenance>;

export const AiToolClaim = z.object({
  /** Evidence first, for the same reason as extraction: a conclusion stated
   *  first gets its support confabulated afterwards. */
  evidenceQuote: z.string().max(2000).nullable().default(null),
  text: z.string().min(1).max(4000),
  provenance: ClaimProvenance,
});
export type AiToolClaim = z.infer<typeof AiToolClaim>;

export const AiToolResult = z.object({
  action: AiToolAction,
  claims: z.array(AiToolClaim).max(100),
  modelVersion: z.string().max(128),
  promptVersion: z.string().max(64),
  generatedAt: z.string(),
});
export type AiToolResult = z.infer<typeof AiToolResult>;

/** Which actions need a scene, so the API can refuse before spending a call. */
export const ACTION_REQUIRES_SCENE: Readonly<Record<AiToolAction, boolean>> = Object.freeze({
  FULL_SCRIPT_SYNOPSIS: false,
  ONE_PAGE_SYNOPSIS: false,
  SCENE_SYNOPSIS: true,
  LOCATION_SUMMARY: false,
  PRODUCTION_COMPLEXITY_SUMMARY: false,
  LOCATION_EFFICIENCY_SUGGESTIONS: false,
  GENERATE_LOCATION_BRIEF: false,
});

/** Which need a requirement. */
export const ACTION_REQUIRES_REQUIREMENT: Readonly<Record<AiToolAction, boolean>> = Object.freeze({
  FULL_SCRIPT_SYNOPSIS: false,
  ONE_PAGE_SYNOPSIS: false,
  SCENE_SYNOPSIS: false,
  LOCATION_SUMMARY: false,
  PRODUCTION_COMPLEXITY_SUMMARY: false,
  LOCATION_EFFICIENCY_SUGGESTIONS: false,
  GENERATE_LOCATION_BRIEF: true,
});

/**
 * AI output is ADVISORY. It is never written into a field a human has confirmed.
 *
 * The rule is enforced structurally rather than by convention: a result is a
 * separate artefact a producer reads and may copy from, and nothing in the tool
 * path writes to a requirement, constraint or production element. The design
 * states it plainly — "AI estimates and suggests; it never silently changes
 * user-confirmed data" — and the moment it did, every confirmation in the
 * product would become provisional.
 */
export const AI_TOOL_OUTPUT_IS_ADVISORY = true;
